// ReportSettings.tsx
import React, { useContext, useEffect, useMemo } from 'react';
import {
  Typography,
  Form,
  Select,
  Row,
  Col,
  Checkbox,
  Space,
  Button,
  message,
} from 'antd';

import { usePatchSettingMutation } from '@/state/slices/setting';
import { useGetTemplatesQuery } from '@/state/slices/reports';
import { Template } from '@/models/template';
import { SettingsContext } from '@/state/contexts';
import HeartLoader from '@/shared/ui/HeartLoader';
import styles from './ReportSettings.module.css';

const { Title, Text } = Typography;

type SectionKey = 'measurements' | 'findings' | 'conclusions';

const sectionMeta: Record<SectionKey, { title: string; subtitle: string }> = {
  measurements: {
    title: 'Measurements Section',
    subtitle: 'LVEF, IVSd, LVIDd, LVPWd, RVSP',
  },
  findings: {
    title: 'Findings Section',
    subtitle: 'Clinical observations and interpretations',
  },
  conclusions: {
    title: 'Conclusions Section',
    subtitle: 'Summary and recommendations',
  },
};

const labelStyle: React.CSSProperties = { color: 'var(--text-secondary)' };
const subStyle: React.CSSProperties = { color: 'var(--text-secondary)' };

const ReportSettings: React.FC = () => {
  const [form] = Form.useForm();
  const { settings } = useContext(SettingsContext);

  const [messageApi, contextHolder] = message.useMessage();

  const { data: templatesResponse, isLoading: isLoadingTemplates } =
    useGetTemplatesQuery();

  const [patchSetting, { isLoading: isSavingTemplate }] =
    usePatchSettingMutation();

  const templates = templatesResponse?.results;

  const defaultTemplateUuid = settings.defaultTemplate;
  const defaultTemplate = templates?.find(
    (t) => t.uuid === defaultTemplateUuid
  );

  const templateOptions = useMemo(
    () =>
      (templates ?? []).map((t: Template) => ({
        label: t.name,
        value: t.uuid,
      })),
    [templates]
  );

  // Initialize form once data is ready
  useEffect(() => {
    if (defaultTemplate) {
      form.setFieldsValue({
        schema: defaultTemplate.name,
      });
    }
  }, [defaultTemplate, form]);

  const handleTemplateChange = async (templateUuid: string) => {
    if (!defaultTemplate) return;

    try {
      if (!settings.uuid) return;
      await patchSetting({
        uuid: settings.uuid,
        data: { defaultTemplate: templateUuid },
      }).unwrap();

      messageApi.success('Default report template updated');
    } catch {
      messageApi.error('Failed to update default template');
    }
  };

  if (isLoadingTemplates) {
    return (
      <div className={styles.panel}>
        <HeartLoader size="sm" />
      </div>
    );
  }

  return (
    <>
      {contextHolder}
      <Title level={3}>Structured Reporting Configuration</Title>

      <div className={styles.panel}>
        <div className={styles.dividerGhost} />

        <Form layout="vertical" form={form}>
          {/* Default template */}
          <Form.Item
            label={<Text style={labelStyle}>Default Report Template</Text>}
            name="schema"
          >
            <Select
              size="large"
              options={templateOptions}
              popupMatchSelectWidth
              showSearch
              optionFilterProp="label"
              loading={isSavingTemplate}
              onChange={handleTemplateChange}
            />
          </Form.Item>

          {(Object.keys(sectionMeta) as SectionKey[]).map((key) => (
            <div key={key} className={styles.block}>
              <Row align="middle" gutter={16} wrap={false}>
                <Col flex="auto">
                  <Space orientation="vertical" size={2}>
                    <Text strong>{sectionMeta[key].title}</Text>
                    <Text style={subStyle}>{sectionMeta[key].subtitle}</Text>
                  </Space>
                </Col>
                <Col>
                  <Form.Item
                    name={['req', key]}
                    valuePropName="checked"
                    noStyle
                  >
                    <Checkbox>
                      <Text style={{ color: 'var(--text-secondary)' }}>
                        Required
                      </Text>
                    </Checkbox>
                  </Form.Item>
                </Col>
              </Row>
            </div>
          ))}

          <Space size="middle" style={{ marginTop: 20 }}>
            <Button size="large" onClick={() => form.resetFields()}>
              Reset
            </Button>
          </Space>
        </Form>
      </div>
    </>
  );
};

export default ReportSettings;
